import React, { useEffect, useRef } from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';
import {
  BiometricStrength,
  isSensorAvailable,
} from '@sbaiahmed1/react-native-biometrics';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import {
  resetBackofficeUnlock,
  selectCurrentRouteName,
  setLastBackgroundAtMs,
  setSecurityGateStatus,
  setSensorInfo,
} from '../../redux/securityRuntimeSlice';
import {
  selectEmployeeRequireBiometricsOnForeground,
  selectBackofficeAccessMode,
  selectAllowDeviceCredentialsFallback,
} from '../../redux/SettingsSlice';
import type { RootState } from '../../redux/types';
import { setEmployeeEmail } from '../../services/SecureCredentials';

/**
 * Runs global security side-effects:
 * - Sensor availability check (cached in runtime redux)
 * - AppState background reset (must reset Back Office TTL on background)
 * - Employee foreground biometric prompt (if enabled)
 */
const SecurityRuntimeController: React.FC = () => {
  const dispatch = useAppDispatch();
  const requireForegroundAuth = useAppSelector(
    selectEmployeeRequireBiometricsOnForeground,
  );
  const backofficeAccessMode = useAppSelector(selectBackofficeAccessMode);
  const allowDeviceCredentialsFallback = useAppSelector(
    selectAllowDeviceCredentialsFallback,
  );

  const employeeId = useAppSelector(
    (s: RootState) => s.auth.inApp.account.employeeInfo?._id,
  );
  const employeeEmail = useAppSelector(
    (s: RootState) =>
      s.auth.inApp.account.employeeInfo?.bp_employee_email_address,
  );
  const biometricInFlight = useAppSelector(
    (s: RootState) => s.securityRuntime.biometricInFlight,
  );
  const sensorChecked = useAppSelector(
    (s: RootState) => s.securityRuntime.sensorInfo.checked,
  );
  const sensorAvailable = useAppSelector(
    (s: RootState) => s.securityRuntime.sensorInfo.info?.available === true,
  );
  const currentRouteName = useAppSelector(selectCurrentRouteName);
  const preventsBiomtericsPrompt = useAppSelector(
    (s: RootState) => s.ui.preventsBiomtericsPrompt,
  );
  const appStateRef = useRef<AppStateStatus>(AppState.currentState);

  const isBackofficeRoute = (name: string | null) => {
    if (!name) return false;
    if (name === 'BackOffice') return true;
    if (name === 'Browser') return true;
    return name.startsWith('BackOffice');
  };

  // Keep employee email synced into Keychain for Back Office auto-login.
  useEffect(() => {
    if (!employeeEmail) return;
    void setEmployeeEmail(String(employeeEmail));
  }, [employeeEmail]);

  // 1) Sensor availability check once per boot
  useEffect(() => {
    if (Platform.OS === 'web') return;
    if (sensorChecked) return;
    let cancelled = false;
    (async () => {
      try {
        const info = await isSensorAvailable({
          biometricStrength: BiometricStrength.Strong,
        });
        if (!cancelled) dispatch(setSensorInfo(info as any));
      } catch (e: any) {
        // store "not available" with error details when possible
        if (!cancelled)
          dispatch(
            setSensorInfo({
              available: false,
              biometryType: 'Unknown',
              error: e?.message,
              errorCode: e?.code || e?.errorCode,
            } as any),
          );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, sensorChecked]);

  // 2) AppState listener
  useEffect(() => {
    if (Platform.OS === 'web') return;

    const onChange = async (next: AppStateStatus) => {
      const prev = appStateRef.current;
      appStateRef.current = next;

      // Reset Back Office unlock immediately when app backgrounds/inactive
      if (
        (next === 'background' || next === 'inactive') &&
        !preventsBiomtericsPrompt
      ) {
        dispatch(setLastBackgroundAtMs(Date.now()));
        dispatch(resetBackofficeUnlock());
        return;
      }

      // Foreground: prompt employees if enabled
      if (prev !== 'active' && next === 'active' && !preventsBiomtericsPrompt) {
        const needsBackofficeResumeAuth =
          backofficeAccessMode === 'biometrics_gate' &&
          isBackofficeRoute(currentRouteName);

        const needsEmployeeAuth = requireForegroundAuth && !!employeeId;

        if (!needsBackofficeResumeAuth && !needsEmployeeAuth) return;
        // If device-credential fallback is allowed, we can still lock/auth
        // even when biometrics aren't available/enrolled.
        if (!sensorAvailable && !allowDeviceCredentialsFallback) return;
        if (biometricInFlight) return;

        // Backoffice resume and employee foreground lock both use navigator gate.
        if (needsBackofficeResumeAuth) {
          dispatch(setSecurityGateStatus('locked'));
        }
        if (needsEmployeeAuth) {
          dispatch(setSecurityGateStatus('locked'));
        }
      }
    };

    const sub = AppState.addEventListener('change', onChange);
    return () => {
      sub.remove();
    };
  }, [
    allowDeviceCredentialsFallback,
    biometricInFlight,
    backofficeAccessMode,
    currentRouteName,
    dispatch,
    employeeId,
    requireForegroundAuth,
    sensorAvailable,
    preventsBiomtericsPrompt,
  ]);

  return null;
};

export default SecurityRuntimeController;
